"use client";

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { Avatar } from '../ui/components/Avatar';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuthStore } from '../store/authStore';
import { getFlipOpportunities, type FlipOpportunity } from '../api/flips';
import { FeatherRefreshCw } from '@subframe/core';

export const FlipsPage: React.FC = () => {
  const [opportunities, setOpportunities] = useState<FlipOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { isAuthenticated: userAuthenticated } = useAuthStore();

  useEffect(() => {
    loadOpportunities();
  }, []);

  const loadOpportunities = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getFlipOpportunities();

      if (response.success && response.data) {
        setOpportunities(response.data.opportunities);
        setIsAuthenticated(response.data.isAuthenticated);
      } else {
        setError('Failed to load flip opportunities');
      }
    } catch (err) {
      console.error('Error loading flip opportunities:', err);
      setError('Failed to load flip opportunities');
    } finally {
      setLoading(false);
    }
  };

  const formatVolume = (volume: number | null) => {
    if (volume === null || volume === undefined) return '–';
    if (volume >= 1000000) {
      return `${(volume / 1000000).toFixed(1)}M`;
    }
    if (volume >= 1000) {
      return `${(volume / 1000).toFixed(1)}K`;
    }
    return volume.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6">
        {/* Header */}
        <div className="flex flex-col gap-4 w-full">
          <div className="flex items-center gap-2">
            <FeatherRefreshCw className="h-8 w-8 text-brand-600" />
            <h1 className="text-heading-2 font-heading-2 text-default-font">
              Flip Opportunities
            </h1>
          </div>
          <p className="text-body font-body text-subtext-color">
            Discover potential buying opportunities from sellers who recently sold NFTs significantly below floor price and still hold other NFTs in the same collection. These motivated sellers may be willing to sell their remaining holdings at a discount.
          </p>
        </div>

        {/* Error state */}
        {error && !loading && (
          <div className="flex w-full items-center justify-center py-8">
            <span className="text-body font-body text-error-700">{error}</span>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex w-full items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && opportunities.length === 0 && (
          <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
            <FeatherRefreshCw className="h-12 w-12 text-neutral-400" />
            <span className="text-body font-body text-subtext-color">
              No flip opportunities found at the moment.
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Check back later for sellers who have sold below floor price.
            </span>
          </div>
        )}

        {/* Opportunities Table */}
        {!loading && !error && opportunities.length > 0 && (
          <div className="w-full overflow-x-auto">
            <div className="relative">
              <table className="w-full border-collapse">
                <thead>
                  {/* Top header row with grouped "Collection Volume" */}
                  <tr className="border-b border-neutral-border">
                    <th rowSpan={2} className="text-left py-2 px-3 text-caption-bold font-caption-bold text-subtext-color align-bottom">
                      NFT
                    </th>
                    <th rowSpan={2} className="text-right py-2 px-3 text-caption-bold font-caption-bold text-subtext-color align-bottom">
                      Floor at Sale
                    </th>
                    <th rowSpan={2} className="text-right py-2 px-3 text-caption-bold font-caption-bold text-subtext-color align-bottom">
                      Sale Price
                    </th>
                    <th rowSpan={2} className="text-right py-2 px-3 text-caption-bold font-caption-bold text-subtext-color align-bottom">
                      Discount
                    </th>
                    <th colSpan={3} className="text-center py-2 px-1 text-caption-bold font-caption-bold text-subtext-color border-b border-neutral-border">
                      Collection Volume (XRP)
                    </th>
                    <th rowSpan={2} className="text-center py-2 px-3 text-caption-bold font-caption-bold text-subtext-color align-bottom">
                      Flip Opps
                    </th>
                  </tr>
                  {/* Sub-header row for volume columns */}
                  <tr className="border-b border-neutral-border">
                    <th className="text-right py-1 px-2 text-caption font-caption text-subtext-color">
                      24h
                    </th>
                    <th className="text-right py-1 px-2 text-caption font-caption text-subtext-color">
                      7d
                    </th>
                    <th className="text-right py-1 px-2 text-caption font-caption text-subtext-color">
                      30d
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map((opp, index) => (
                    <tr
                      key={`${opp.seller_address}-${opp.collection_id}-${index}`}
                      className={`border-b border-neutral-border h-20 ${!isAuthenticated ? 'blur-sm select-none pointer-events-none' : ''}`}
                    >
                      {/* NFT Cell */}
                      <td className="py-3 px-3">
                        <Link
                          to={`/nfts/${opp.nft_id}`}
                          className="flex items-center gap-3 hover:opacity-80"
                        >
                          <Avatar
                            size="large"
                            image={opp.nft_thumbnail_url || undefined}
                            square={true}
                          >
                            N
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="text-body-bold font-body-bold text-default-font">
                              {opp.nft_name || 'Unnamed NFT'}
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              {opp.collection_name}
                            </span>
                          </div>
                        </Link>
                      </td>
                      {/* Floor at Sale */}
                      <td className="py-3 px-3 text-right">
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {opp.floor_price_at_sale.toLocaleString()} XRP
                        </span>
                      </td>
                      {/* Sale Price */}
                      <td className="py-3 px-3 text-right">
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {opp.sale_price.toLocaleString()} XRP
                        </span>
                      </td>
                      {/* Discount (combined XRP and %) */}
                      <td className="py-3 px-3 text-right">
                        <div className="flex flex-col items-end">
                          <span className="whitespace-nowrap text-body-bold font-body-bold text-success-600">
                            {opp.xrp_below_floor.toLocaleString()} XRP
                          </span>
                          <span className="whitespace-nowrap text-caption font-caption text-success-600">
                            {opp.discount_percentage.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      {/* Volume 24h */}
                      <td className="py-3 px-2 text-right">
                        <span className="whitespace-nowrap text-body font-body text-subtext-color">
                          {formatVolume(opp.collection_volume_24h)}
                        </span>
                      </td>
                      {/* Volume 7d */}
                      <td className="py-3 px-2 text-right">
                        <span className="whitespace-nowrap text-body font-body text-subtext-color">
                          {formatVolume(opp.collection_volume_7d)}
                        </span>
                      </td>
                      {/* Volume 30d */}
                      <td className="py-3 px-2 text-right">
                        <span className="whitespace-nowrap text-body font-body text-subtext-color">
                          {formatVolume(opp.collection_volume_30d)}
                        </span>
                      </td>
                      {/* Available NFTs - CTA button */}
                      <td className="py-3 px-3 text-center">
                        <Link
                          to={`/wallets/${opp.seller_address}/${opp.collection_creator_address}:${opp.collection_taxon}?tab=nfts`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-600 hover:bg-brand-700 text-white text-caption-bold font-caption-bold transition-colors"
                        >
                          {opp.remaining_nft_count} NFTs
                          <span className="text-xs">→</span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Blur overlay for non-authenticated users */}
              {!isAuthenticated && opportunities.length > 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-default-background/60 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-4 p-6 rounded-lg border border-neutral-border bg-default-background shadow-lg max-w-md text-center">
                    <FeatherRefreshCw className="h-10 w-10 text-brand-600" />
                    <h3 className="text-heading-3 font-heading-3 text-default-font">
                      Sign in to view flip opportunities
                    </h3>
                    <p className="text-body font-body text-subtext-color">
                      Create a free account to see which sellers recently dumped NFTs below floor price and might be willing to sell their remaining holdings at a discount.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info note */}
        {!loading && !error && opportunities.length > 0 && isAuthenticated && (
          <div className="w-full p-4 rounded-lg border border-neutral-border bg-default-background">
            <p className="text-caption font-caption text-subtext-color">
              <strong>How to use:</strong> Click the "NFTs →" button to view the seller's remaining NFTs in that collection. You can then make lowball offers on their holdings. Sellers who sold below floor may be motivated to accept discounted offers.
            </p>
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
};
